import { Component } from '@angular/core';
import {
  DataStateChangeEvent,
  GridDataResult,
  FilterService,
} from '@progress/kendo-angular-grid';
import {
  ColumnOptions,
  GridState,
  LoaderService,
  MessageService,
  ValidationResultService,
} from '@shared';
import {
  ChangesetService,
  GlobalEventsService,
  Tier,
  TiersService,
} from '@shared-data';
import { GridRowPriceMismatch } from '../../models';
import { GroupDescriptor, CompositeFilterDescriptor } from '@progress/kendo-data-query';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-audit',
  templateUrl: './audit.component.html',
  styleUrl: './audit.component.scss',
})
export class AuditComponent {
  gridData: GridDataResult = { data: [], total: 0 };

  group: GroupDescriptor[] = [{ field: 'groupKey' }];

  pageSize = 100;
  state = new GridState(this.pageSize);

  columns: ColumnOptions[] = [];

  tiers: Tier[] = [];

  selectedTier: Tier = new Tier({ id: '-1', name: 'None' });

  fontSize: number = 0.875;

  tierAlignEnabled = environment.tierAlignEnabled;

  get customFilterView(): string {
    return 'audit/' + this.selectedTier.id;
  }

  filterCache: Map<string, string[]> = new Map<string, string[]>();

  onCustomFilterChange(
    column: string,
    event: string[],
    filterService: FilterService,
  ) {
    this.filterCache.set(column, event);

    filterService.filter({
      filters: event.map((value) => ({
        field: column,
        operator: 'eq',
        value,
      })),
      logic: 'or',
    });
  }

  filterChanged(event: CompositeFilterDescriptor) {
    if (event.filters.length === 0) {
      this.filterCache.clear();
    }

    for (const field of this.filterCache.keys()) {
      if (!GridState.hasField(event, field)) {
        this.filterCache.delete(field);
      }
    }
  }

  constructor(
    private changesetService: ChangesetService,
    private messageService: MessageService,
    private loaderService: LoaderService,
    private tierService: TiersService,
    private globalEventsService: GlobalEventsService,
    private validationErrorService: ValidationResultService,
  ) {
    this.columns = new GridRowPriceMismatch().getColumns();
  }

  ngOnInit() {
    this.getTiers();
  }

  onDataStateChanged(event: DataStateChangeEvent) {
    this.fetchMismatches(event as GridState);
  }

  setDefaultState() {
    this.filterCache.clear();
    this.state = new GridState(this.pageSize);
    this.fetchMismatches(this.state);
  }

  getTiers() {
    this.loaderService.show();

    this.tierService.getTiers().subscribe({
      next: (tiers) => {
        this.tiers = tiers;
        this.selectedTier = this.tiers[0];
        this.loaderService.hide();

        this.fetchMismatches(this.state);
      },
      error: (error) => {
        this.messageService.error(`Failed to get tiers`);
        var validationError = error?.error;
        if (validationError) {
          this.validationErrorService.show(validationError);
        }
        this.loaderService.hide();
      },
    });
  }

  fetchMismatches(state: GridState) {
    this.loaderService.show();
    if (state.filter !== undefined) {
      this.state.filter = state.filter;
    }
    if (state.skip !== undefined) {
      this.state.skip = state.skip;
    }
    if (state.take !== undefined) {
      this.state.take = state.take;
    }
    if (state.sort?.length) {
      this.state.sort = state.sort;
    } else {
      this.state.sort = [{ field: 'restaurantNo', dir: 'asc' }];
    }

    this.state.group = this.group;

    this.changesetService
      .getPriceMismatches(this.selectedTier.id, this.state)
      .subscribe({
        next: (mismatches) => {
          this.gridData = mismatches;
          this.loaderService.hide();
        },
        error: (error) => {
          this.messageService.error('Failed to load price mismatches');
          var validationError = error?.error;
          if (validationError) {
            this.validationErrorService.show(validationError);
          }
          this.loaderService.hide();
        },
      });
  }

  fixMismatch(mismatch: GridRowPriceMismatch) {
    this.loaderService.show();
    this.changesetService
      .fixPriceMismatch(mismatch.kingRestaurantId, mismatch)
      .subscribe({
        next: () => {
          this.messageService.success(
            `Price mismatch for ${mismatch.restaurantNo} fixed successfully,
           please sync changes`,
          );
          this.fetchMismatches(this.state);
          this.globalEventsService.updatePendingChanges();
          this.loaderService.hide();
        },
        error: (error) => {
          this.messageService.error(
            `Failed to fix price mismatch for ${mismatch.restaurantNo}`,
          );

          var validationError = error?.error;
          if (validationError) {
            this.validationErrorService.show(validationError);
          }
          this.loaderService.hide();
        },
      });
  }

  alignTier() {
    this.loaderService.show();
    this.tierService
      .syncTier(
        this.selectedTier.id
      )
      .subscribe({
        next: () => {
          this.messageService
            .success(`Tier ${this.selectedTier.name} aligned successfully,
           please sync changes`);
          this.fetchMismatches(this.state);
          this.globalEventsService.updatePendingChanges();
          this.loaderService.hide();
        },
        error: (error) => {
          this.messageService.error(
            `Failed to align tier ${this.selectedTier.name}`,
          );
          var validationError = error?.error;
          if (validationError) {
            this.validationErrorService.show(validationError);
          }
          this.loaderService.hide();
        },
      });
  }

  exportTierMismatches() {
    this.loaderService.show();
    this.changesetService
      .exportPriceMismatches(this.selectedTier.id)
      .subscribe({
        next: (data: Blob) => {
          const blob = new Blob([data], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${this.selectedTier.name}_Mismatches_${new Date().toDateString()}.xlsx`;
          document.body.appendChild(a); // Append to body to ensure it works in some browsers
          a.click();
          document.body.removeChild(a); // Remove element after click
          window.URL.revokeObjectURL(url);
          this.loaderService.hide();
        },
        error: (error) => {
          this.loaderService.hide();
          this.messageService.error('Failed to export mismatches');
          var validationError = error?.error;
          if (validationError) {
            this.validationErrorService.show(validationError);
          }
        },
      });
  }

  exportAllMismatches() {
    this.loaderService.show();
    this.changesetService.exportAllMismatches().subscribe({
      next: (data: Blob) => {
        const blob = new Blob([data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `(All) Mismatches_${new Date().toDateString()}.xlsx`;
        document.body.appendChild(a); // Append to body to ensure it works in some browsers
        a.click();
        document.body.removeChild(a); // Remove element after click
        window.URL.revokeObjectURL(url);
        this.loaderService.hide();
      },
      error: (error) => {
        this.loaderService.hide();
        this.messageService.error('Failed to export mismatches');
        var validationError = error?.error;
        if (validationError) {
          this.validationErrorService.show(validationError);
        }
      },
    });
  }
}
